import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronDown, ChevronRight, Plus, Trash2, GripVertical } from "lucide-react";

interface WBSNode {
  id: string;
  code: string;
  name: string;
  owner: string;
  hours: number;
  children: WBSNode[];
  collapsed: boolean;
}

function makeNode(code: string): WBSNode {
  return { id: crypto.randomUUID(), code, name: "", owner: "", hours: 0, children: [], collapsed: false };
}

function countAll(nodes: WBSNode[]): number {
  return nodes.reduce((s, n) => s + 1 + countAll(n.children), 0);
}

function sumHours(nodes: WBSNode[]): number {
  return nodes.reduce((s, n) => s + (n.hours || 0) + sumHours(n.children), 0);
}

function updateNode(nodes: WBSNode[], id: string, updater: (n: WBSNode) => WBSNode): WBSNode[] {
  return nodes.map(n => n.id === id ? updater(n) : { ...n, children: updateNode(n.children, id, updater) });
}

function deleteNode(nodes: WBSNode[], id: string): WBSNode[] {
  return nodes.filter(n => n.id !== id).map(n => ({ ...n, children: deleteNode(n.children, id) }));
}

function addChild(nodes: WBSNode[], parentId: string, child: WBSNode): WBSNode[] {
  return nodes.map(n => n.id === parentId
    ? { ...n, children: [...n.children, child], collapsed: false }
    : { ...n, children: addChild(n.children, parentId, child) }
  );
}

const LEVEL_COLORS = [
  "border-l-[#1e3a6e] bg-[#1e3a6e]/5",
  "border-l-[#1e5fa6] bg-[#1e5fa6]/5",
  "border-l-[#2e7fd0] bg-[#2e7fd0]/5",
  "border-l-[#60aae8] bg-[#60aae8]/5",
];

function NodeRow({ node, depth, onUpdate, onDelete, onAddChild }:
  { node: WBSNode; depth: number; onUpdate: (id: string, u: (n: WBSNode) => WBSNode) => void; onDelete: (id: string) => void; onAddChild: (parentId: string) => void }) {

  const color = LEVEL_COLORS[Math.min(depth, 3)];
  const hasChildren = node.children.length > 0;
  const childHours = sumHours(node.children);
  const totalHours = (node.hours || 0) + childHours;

  return (
    <div>
      <div className={`flex items-center gap-2 border-l-4 ${color} rounded-r-md px-3 py-2 mb-1 group`}
        style={{ marginLeft: depth * 20 }}>
        <GripVertical className="h-3.5 w-3.5 text-muted-foreground/30 shrink-0" />
        <button
          onClick={() => onUpdate(node.id, n => ({ ...n, collapsed: !n.collapsed }))}
          className="shrink-0 text-muted-foreground hover:text-foreground"
        >
          {hasChildren
            ? node.collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />
            : <span className="w-3.5 inline-block" />}
        </button>

        <Input value={node.code} onChange={e => onUpdate(node.id, n => ({ ...n, code: e.target.value }))}
          className="h-6 w-16 text-[11px] font-mono border-0 bg-transparent p-0 focus-visible:ring-0 text-muted-foreground"
          placeholder="1.1.1" />

        <Input value={node.name} onChange={e => onUpdate(node.id, n => ({ ...n, name: e.target.value }))}
          className={`h-6 flex-1 border-0 bg-transparent p-0 focus-visible:ring-0 ${depth === 0 ? "text-sm font-semibold" : "text-[12px]"}`}
          placeholder={depth === 0 ? "Deliverable name…" : depth === 1 ? "Work package…" : "Activity…"} />

        <Input value={node.owner} onChange={e => onUpdate(node.id, n => ({ ...n, owner: e.target.value }))}
          className="h-6 w-24 text-[11px] border-0 bg-transparent p-0 focus-visible:ring-0 text-muted-foreground"
          placeholder="Owner" />

        <Input type="number" value={node.hours || ""} onChange={e => onUpdate(node.id, n => ({ ...n, hours: +e.target.value }))}
          className="h-6 w-16 text-[11px] font-mono border-0 bg-transparent p-0 focus-visible:ring-0 text-right text-muted-foreground"
          placeholder="hrs" />
        {childHours > 0 && (
          <span className="text-[10px] font-mono text-muted-foreground/60 shrink-0">({totalHours}h)</span>
        )}

        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          {depth < 3 && (
            <button onClick={() => onAddChild(node.id)} className="text-muted-foreground hover:text-foreground" title="Add child">
              <Plus className="h-3 w-3" />
            </button>
          )}
          <button onClick={() => onDelete(node.id)} className="text-muted-foreground hover:text-destructive" title="Delete">
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>

      {!node.collapsed && node.children.map(child => (
        <NodeRow key={child.id} node={child} depth={depth + 1}
          onUpdate={onUpdate} onDelete={onDelete} onAddChild={onAddChild} />
      ))}
    </div>
  );
}

export default function WBSPanel() {
  const [nodes, setNodes] = useState<WBSNode[]>([]);

  const update = (id: string, updater: (n: WBSNode) => WBSNode) =>
    setNodes(prev => updateNode(prev, id, updater));

  const del = (id: string) => setNodes(prev => deleteNode(prev, id));

  const addChild = (parentId: string) => {
    const newNode = makeNode("");
    setNodes(prev => {
      const addToParent = (nodes: WBSNode[]): WBSNode[] =>
        nodes.map(n => n.id === parentId
          ? { ...n, children: [...n.children, newNode], collapsed: false }
          : { ...n, children: addToParent(n.children) }
        );
      return addToParent(prev);
    });
  };

  const addRoot = () => setNodes(prev => [...prev, makeNode(`${prev.length + 1}`)]);

  const total = sumHours(nodes);
  const count = countAll(nodes);

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 border-b border-border px-5 py-3 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold">Work Breakdown Structure</h2>
          <p className="text-[11px] text-muted-foreground">
            {count > 0 ? `${count} nodes · ${total}h estimated` : "Decompose project into deliverables → work packages → activities."}
          </p>
        </div>
        <Button size="sm" variant="outline" className="h-7 gap-1 text-[11px]" onClick={addRoot}>
          <Plus className="h-3 w-3" /> Add Deliverable
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4" style={{ scrollbarWidth: "thin" }}>
        {nodes.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <p className="text-sm font-medium">No WBS yet</p>
            <p className="text-[11px] mt-1">Click "Add Deliverable" to start. Hover any node to add children or delete.</p>
            <div className="mt-6 text-[11px] text-muted-foreground/60 font-mono space-y-1 text-left max-w-xs mx-auto">
              <p>Level 1 — Deliverable (e.g. "Data Migration")</p>
              <p className="pl-4">Level 2 — Work Package (e.g. "Field Mapping")</p>
              <p className="pl-8">Level 3 — Activity (e.g. "Map 47 MedTrack fields")</p>
              <p className="pl-12">Level 4 — Task</p>
            </div>
          </div>
        ) : (
          <div className="space-y-0.5">
            {/* Column headers */}
            <div className="flex items-center gap-2 px-3 pb-1 text-[9px] font-mono uppercase text-muted-foreground/50 ml-8">
              <span className="w-16">Code</span>
              <span className="flex-1">Name</span>
              <span className="w-24">Owner</span>
              <span className="w-16 text-right">Est. hrs</span>
              <span className="w-8"></span>
            </div>
            {nodes.map(node => (
              <NodeRow key={node.id} node={node} depth={0}
                onUpdate={update} onDelete={del} onAddChild={addChild} />
            ))}
            {/* Total row */}
            {total > 0 && (
              <div className="flex items-center gap-2 px-3 pt-3 border-t border-border mt-2 text-[11px] font-mono">
                <span className="flex-1 text-muted-foreground">TOTAL ESTIMATED</span>
                <span className="font-bold">{total}h</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
